import { createFeatureSelector, createSelector } from '@ngrx/store';
import { WebSocketState, RealTimePriceState } from '../reducers/websocket.reducer';

// Feature selectors
export const selectWebSocketState = createFeatureSelector<WebSocketState>('webSocket');
export const selectRealTimePriceState = createFeatureSelector<RealTimePriceState>('realTimePrice');

// WebSocket Selectors
export const selectConnectionStatus = createSelector(
  selectWebSocketState,
  (state) => state.connectionStatus
);

export const selectIsConnected = createSelector(
  selectConnectionStatus,
  (status) => status.isConnected
);

export const selectIsConnecting = createSelector(
  selectWebSocketState,
  (state) => state.isConnecting
);

export const selectIsReconnecting = createSelector(
  selectConnectionStatus,
  (status) => status.isReconnecting
);

export const selectConnectionError = createSelector(
  selectWebSocketState,
  (state) => state.connectionError
);

export const selectSubscribedProducts = createSelector(
  selectWebSocketState,
  (state) => state.subscribedProducts
);

export const selectIsSubscribedToDashboard = createSelector(
  selectWebSocketState,
  (state) => state.subscribedToDashboard
);

export const selectQueuedMessagesCount = createSelector(
  selectWebSocketState,
  (state) => state.queuedMessages.length
);

export const selectLastPriceUpdates = createSelector(
  selectWebSocketState,
  (state) => state.lastPriceUpdates
);

export const selectLastCompetitorUpdate = createSelector(
  selectWebSocketState,
  (state) => state.lastCompetitorUpdate
);

export const selectSystemAlerts = createSelector(
  selectWebSocketState,
  (state) => state.systemAlerts
);

export const selectMessagesPerSecond = createSelector(
  selectWebSocketState,
  (state) => state.messagesPerSecond
);

export const selectWebSocketPerformanceMetrics = createSelector(
  selectWebSocketState,
  (state) => ({
    messagesReceived: state.messagesReceived,
    messagesPerSecond: state.messagesPerSecond,
    lastMessageTimestamp: state.lastMessageTimestamp,
    queuedMessages: state.queuedMessages.length
  })
);

// Real-time Price Selectors
export const selectAllProductPrices = createSelector(
  selectRealTimePriceState,
  (state) => Object.values(state.productPrices)
);

export const selectProductPriceById = (productId: string) => createSelector(
  selectRealTimePriceState,
  (state) => state.productPrices[productId]
);

export const selectProductPricesByIds = (productIds: string[]) => createSelector(
  selectRealTimePriceState,
  (state) => productIds.map(id => state.productPrices[id]).filter(Boolean)
);

export const selectPriceAlerts = createSelector(
  selectRealTimePriceState,
  (state) => state.priceAlerts
);

export const selectActiveAlerts = createSelector(
  selectPriceAlerts,
  (alerts) => alerts.filter(alert => !alert.acknowledged)
);

export const selectCriticalAlerts = createSelector(
  selectActiveAlerts,
  (alerts) => alerts.filter(alert => alert.severity === 'critical')
);

export const selectWarningAlerts = createSelector(
  selectActiveAlerts,
  (alerts) => alerts.filter(alert => alert.severity === 'warning')
);

export const selectInfoAlerts = createSelector(
  selectActiveAlerts,
  (alerts) => alerts.filter(alert => alert.severity === 'info')
);

export const selectActiveAlertsCount = createSelector(
  selectRealTimePriceState,
  (state) => state.activeAlertsCount
);

export const selectAcknowledgedAlertsCount = createSelector(
  selectRealTimePriceState,
  (state) => state.acknowledgedAlertsCount
);

export const selectMarketMetrics = createSelector(
  selectRealTimePriceState,
  (state) => state.marketMetrics
);

export const selectMonitoredProducts = createSelector(
  selectRealTimePriceState,
  (state) => state.monitoredProducts
);

export const selectRealTimePriceError = createSelector(
  selectRealTimePriceState,
  (state) => state.error
);

export const selectIsRealTimePriceLoading = createSelector(
  selectRealTimePriceState,
  (state) => state.isLoading
);

// Combined Selectors
export const selectRealtimeConnectionInfo = createSelector(
  selectIsConnected,
  selectIsReconnecting,
  selectConnectionError,
  selectQueuedMessagesCount,
  (isConnected, isReconnecting, error, queuedMessages) => ({
    isConnected,
    isReconnecting,
    hasError: !!error,
    error,
    queuedMessages,
    status: isConnected ? 'connected' : isReconnecting ? 'reconnecting' : 'disconnected'
  })
);

export const selectDashboardRealtimeData = createSelector(
  selectMarketMetrics,
  selectActiveAlertsCount,
  selectAllProductPrices,
  (metrics, alertsCount, productPrices) => ({
    metrics,
    alertsCount,
    totalProductsWithPrices: productPrices.length,
    averagePriceChange: metrics.averagePriceChange,
    marketTrend: metrics.marketTrend,
    volatilityIndex: metrics.volatilityIndex
  })
);

export const selectProductRealtimeData = (productId: string) => createSelector(
  selectProductPriceById(productId),
  selectPriceAlerts,
  (productPrice, alerts) => {
    const productAlerts = alerts.filter(alert => alert.productId === productId);
    return {
      priceData: productPrice,
      alerts: productAlerts,
      activeAlerts: productAlerts.filter(alert => !alert.acknowledged),
      hasRecentUpdate: productPrice ? (Date.now() - productPrice.lastUpdated) < 60000 : false
    };
  }
);

export const selectCompetitorInsights = createSelector(
  selectAllProductPrices,
  (productPrices) => {
    const insights = {
      totalCompetitors: 0,
      averageCompetitorCount: 0,
      productsWithLowestPrice: 0,
      productsUndercut: 0,
      competitorsByMarketplace: {} as { [key: string]: number }
    };

    if (productPrices.length === 0) return insights;

    let totalCompetitors = 0;
    let productsWithLowestPrice = 0;
    let productsUndercut = 0;
    const marketplaceCounts: { [key: string]: number } = {};

    productPrices.forEach(product => {
      const competitors = product.competitors || [];
      totalCompetitors += competitors.length;

      // Check if our product has the lowest price
      const lowestCompetitorPrice = Math.min(...competitors.map(c => c.price));
      if (product.currentPrice <= lowestCompetitorPrice) {
        productsWithLowestPrice++;
      } else {
        productsUndercut++;
      }

      // Count competitors by marketplace
      competitors.forEach(competitor => {
        marketplaceCounts[competitor.marketplace] = 
          (marketplaceCounts[competitor.marketplace] || 0) + 1;
      });
    });

    insights.totalCompetitors = totalCompetitors;
    insights.averageCompetitorCount = totalCompetitors / productPrices.length;
    insights.productsWithLowestPrice = productsWithLowestPrice;
    insights.productsUndercut = productsUndercut;
    insights.competitorsByMarketplace = marketplaceCounts;

    return insights;
  }
);

export const selectPriceVolatilityAnalysis = createSelector(
  selectAllProductPrices,
  (productPrices) => {
    const analysis = {
      highVolatilityProducts: 0,
      mediumVolatilityProducts: 0,
      lowVolatilityProducts: 0,
      averageVolatilityIndex: 0,
      mostVolatileProducts: [] as any[]
    };

    if (productPrices.length === 0) return analysis;

    productPrices.forEach(product => {
      switch (product.volatility) {
        case 'high':
          analysis.highVolatilityProducts++;
          break;
        case 'medium':
          analysis.mediumVolatilityProducts++;
          break;
        case 'low':
          analysis.lowVolatilityProducts++;
          break;
      }
    });

    // Find most volatile products (those with high volatility and recent changes)
    analysis.mostVolatileProducts = productPrices
      .filter(product => product.volatility === 'high')
      .sort((a, b) => Math.abs(b.changePercent) - Math.abs(a.changePercent))
      .slice(0, 5)
      .map(product => ({
        productId: product.productId,
        changePercent: product.changePercent,
        volatility: product.volatility,
        lastUpdated: product.lastUpdated
      }));

    return analysis;
  }
);